import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/** Merge Tailwind classes with clsx */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format price in rubles: 1500 → "1 500 ₽" */
export function formatPrice(price: number): string {
  return price.toLocaleString("ru-RU") + " ₽";
}

/** Format date to Russian locale */
export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Format short date: "12.02.2026" */
export function formatDateShort(date: string | Date): string {
  return new Date(date).toLocaleDateString("ru-RU");
}

/** Calculate available count (total - booked) */
export function availableCount(count: number, booked: number): number {
  return Math.max(0, count - booked);
}

/** Plural form for Russian: items(1) = "товар", items(2) = "товара", items(5) = "товаров" */
export function pluralize(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const lastDigit = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (lastDigit > 1 && lastDigit < 5) return few;
  if (lastDigit === 1) return one;
  return many;
}

/** Stock status label map */
export const STOCK_STATUS_LABELS: Record<string, string> = {
  in_stock: "В наличии",
  in_transit: "В пути",
};

/** Stock status color class map */
export const STOCK_STATUS_COLORS: Record<string, string> = {
  in_stock: "text-success",
  in_transit: "text-warning",
};

/** Format datetime short: "12.02.2026, 15:30" */
export function formatDateTimeShort(date: string | Date): string {
  return new Date(date).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Moscow timezone utilities (Europe/Moscow = UTC+3, no DST) ──

const MOSCOW_TZ = "Europe/Moscow";

/** Format ISO datetime as Moscow time: "15.03.2026, 14:30 МСК" */
export function formatMoscowDateTime(isoString: string): string {
  return (
    new Intl.DateTimeFormat("ru-RU", {
      timeZone: MOSCOW_TZ,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(isoString)) + " МСК"
  );
}

/**
 * Convert a datetime-local value (assumed Moscow time) to UTC ISO string.
 * Moscow is always UTC+3 (no DST since 2014).
 * Input: "2026-03-15T14:30" → Output: "2026-03-15T11:30:00.000Z"
 */
export function moscowLocalToUTC(datetimeLocalValue: string): string {
  const moscowDate = new Date(datetimeLocalValue + ":00+03:00");
  return moscowDate.toISOString();
}

/**
 * Convert UTC ISO string to datetime-local value in Moscow time.
 * Used to pre-fill datetime-local input when rescheduling.
 */
export function utcToMoscowLocal(isoString: string): string {
  const date = new Date(isoString);
  // sv-SE locale gives "YYYY-MM-DD HH:mm" format
  const moscowStr = date.toLocaleString("sv-SE", {
    timeZone: MOSCOW_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  return moscowStr.replace(" ", "T");
}

/** Get minimum datetime-local value (current Moscow time, rounded up to next 5 min). */
export function getMinMoscowDatetime(): string {
  const now = new Date();
  now.setMinutes(Math.ceil(now.getMinutes() / 5) * 5, 0, 0);
  return utcToMoscowLocal(now.toISOString());
}
